import { useState, useEffect, useRef, Fragment } from "react";
import { toPng } from "html-to-image";
import { Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar,
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, Plus, Trash2, Loader2, ShieldAlert, TrendingDown,
  Wallet, Sparkles, CheckCircle2, ListChecks, Upload, Download, History,
  Mail, Send, TrendingUp, FileText, Settings, AlertTriangle, StickyNote, Eye, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import BusinessIntelAssessment from "../components/site/BusinessIntelAssessment";
import ConstructionSimulation from "../components/site/ConstructionSimulation";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#c9a961]/60 focus:outline-none transition-colors duration-300";

const COLORS = ["#c9a961", "#10b981", "#f59e0b", "#a78bfa", "#f472b6", "#22d3ee", "#fb7185", "#84cc16", "#e879f9", "#facc15"];
const sev = { Low: "#10b981", Medium: "#c9a961", High: "#f59e0b", Critical: "#ef4444" };
const emptyRow = () => ({ label: "", amount: "", frequency: "monthly", category: "" });

// Interactive charts for the consolidated vault analysis. Click donut segments
// (or legend rows) to include/exclude a category, and use the market-trend
// controls to re-populate 12-month projections up or down.
function VaultCharts({ data, fmt, onApplyCategory, onApplyAll }) {
  const cats = (data.expense_breakdown || []).map((b, i) => ({
    label: b.label || `Item ${i + 1}`, amount: Number(b.amount) || 0, projected: Number(b.projected) || 0,
  }));
  const [active, setActive] = useState(() => new Set(cats.map((c) => c.label)));
  const [growth, setGrowth] = useState(Number(data.market_outlook?.monthly_growth_pct) || 0);
  const months = 12;
  const toggle = (label) => setActive((s) => {
    const n = new Set(s); n.has(label) ? n.delete(label) : n.add(label); return n;
  });
  const activeCats = cats.filter((c) => active.has(c.label));
  const baseCurrent = activeCats.reduce((s, c) => s + c.amount, 0);
  const baseProjected = activeCats.reduce((s, c) => s + c.projected, 0);
  const g = growth / 100;
  const series = Array.from({ length: months + 1 }, (_, m) => ({
    month: `M${m}`,
    Current: Math.round(baseCurrent * Math.pow(1 + g, m)),
    "With Elevare": Math.round(baseProjected * Math.pow(1 + g, m)),
  }));
  const end = series[months];
  const saveNow = Math.round(baseCurrent - baseProjected);
  const saveEnd = end.Current - end["With Elevare"];
  const trendBtn = (label, val) => (
    <button
      key={label}
      data-testid={`trend-${label.toLowerCase()}`}
      onClick={() => setGrowth(val)}
      className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${Math.abs(growth - val) < 0.01 ? "bg-[#c9a961] text-[#1a2e2b]" : "bg-white/10 text-white/60 hover:text-white"}`}
    >{label}</button>
  );
  return (
    <div className="mt-4 space-y-4" id="vault-charts-capture" data-testid="vault-charts">
      {/* Market trend controls */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/45">Market trend — populate projections</span>
          <div className="flex items-center gap-1.5">
            {trendBtn("Down", -3)}{trendBtn("Flat", 0)}{trendBtn("Up", 3)}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <input
            data-testid="trend-slider" type="range" min="-8" max="8" step="0.5" value={growth}
            onChange={(e) => setGrowth(Number(e.target.value))} className="flex-1 accent-[#c9a961]"
          />
          <span className="tabular-nums text-xs text-white/70 w-24 text-right">{growth > 0 ? "+" : ""}{growth}%/mo</span>
        </div>
        {data.market_outlook?.rationale && (
          <p className="mt-1.5 text-[11px] text-white/50 leading-relaxed">{data.market_outlook.rationale}</p>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Clickable donut */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-white/45 mb-1">Expense mix — click to include/exclude</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={cats} dataKey="amount" nameKey="label" innerRadius={45} outerRadius={80}
                onClick={(e) => e && e.label && toggle(e.label)} isAnimationActive={false}
              >
                {cats.map((c, i) => (
                  <Cell key={i} cursor="pointer" fill={COLORS[i % COLORS.length]}
                        fillOpacity={active.has(c.label) ? 1 : 0.2} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#12211f", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {cats.map((c, i) => (
              <span key={i} className={`inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-[10px] transition-opacity ${active.has(c.label) ? "opacity-100" : "opacity-40"}`}
                style={{ background: `${COLORS[i % COLORS.length]}22`, color: COLORS[i % COLORS.length] }}>
                <button data-testid={`cat-toggle-${i}`} onClick={() => toggle(c.label)} className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c.label}
                </button>
                {onApplyCategory && (
                  <button data-testid={`cat-apply-${i}`} title="Add to worksheet" onClick={() => onApplyCategory(c)}
                    className="ml-0.5 rounded-full bg-white/10 px-1 leading-none hover:bg-white/25">+</button>
                )}
              </span>
            ))}
          </div>
          {onApplyAll && (
            <button data-testid="charts-apply-all" onClick={() => onApplyAll(cats)}
              className="mt-2 w-full rounded-lg border border-dashed border-white/15 py-1.5 text-[11px] text-white/60 hover:text-[#c9a961] hover:border-[#c9a961]/40 transition-colors">
              + Drop all categories into the worksheet
            </button>
          )}
        </div>

        {/* Current vs projected bars */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-white/45 mb-1">Current vs. with Elevare</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={activeCats.slice(0, 7)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,.4)", fontSize: 9 }} interval={0} hide />
              <YAxis tick={{ fill: "rgba(255,255,255,.4)", fontSize: 9 }} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#12211f", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="amount" name="Current" fill="#c9a961" radius={[3, 3, 0, 0]} />
              <Bar dataKey="projected" name="With Elevare" fill="#7fd1ae" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 12-month projection */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="vault-projection-chart">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/45">12-month projection @ {growth > 0 ? "+" : ""}{growth}%/mo</span>
          <span className="text-[11px] text-white/60">Save now <b className="text-[#7fd1ae]">{fmt(saveNow)}/mo</b> · at M12 <b className="text-[#7fd1ae]">{fmt(saveEnd)}/mo</b></span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={series}>
            <defs>
              <linearGradient id="gCur" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c9a961" stopOpacity={0.5} /><stop offset="100%" stopColor="#c9a961" stopOpacity={0} /></linearGradient>
              <linearGradient id="gElv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7fd1ae" stopOpacity={0.5} /><stop offset="100%" stopColor="#7fd1ae" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
            <XAxis dataKey="month" tick={{ fill: "rgba(255,255,255,.4)", fontSize: 9 }} />
            <YAxis tick={{ fill: "rgba(255,255,255,.4)", fontSize: 9 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#12211f", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Current" stroke="#c9a961" fill="url(#gCur)" strokeWidth={2} />
            <Area type="monotone" dataKey="With Elevare" stroke="#7fd1ae" fill="url(#gElv)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


function getClientId() {
  let id = localStorage.getItem("elevare_client_id");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
    localStorage.setItem("elevare_client_id", id);
  }
  return id;
}

export default function ExpenseIntelGuard() {
  // Admins authenticate one of two ways: the legacy shared secret (stored as
  // `elevare_admin_secret`) or a per-user JWT session (httpOnly cookie +
  // `elevare_admin_user` in localStorage). Accept either — checking only the
  // secret locked out every JWT-based admin and sent them back to /admin.
  const isAdmin =
    typeof window !== "undefined" &&
    (!!localStorage.getItem("elevare_admin_secret") ||
      !!localStorage.getItem("elevare_admin_user"));
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return <ExpenseIntel />;
}

function ExpenseIntel() {
  const [businessName, setBusinessName] = useState("");
  const [revenue, setRevenue] = useState("");
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [history, setHistory] = useState([]);
  const [csvPreview, setCsvPreview] = useState(null);
  const [categorizing, setCategorizing] = useState(false);
  const [notes, setNotes] = useState("");
  const [budgets, setBudgets] = useState([]); // [{category, limit}]
  const [showSettings, setShowSettings] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipInput, setRecipInput] = useState("");
  const [savingRecipients, setSavingRecipients] = useState(false);
  const clientId = useRef(getClientId()).current;
  const fileRef = useRef(null);
  const vaultRef = useRef(null);
  const [clientFiles, setClientFiles] = useState([]);
  const [vaultUploading, setVaultUploading] = useState(false);
  const [vaultDragOver, setVaultDragOver] = useState(false);
  const [vaultProgress, setVaultProgress] = useState(null); // { pct, loaded, total, name }
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analyses, setAnalyses] = useState({}); // fileId -> analysis
  const [vaultAnalysis, setVaultAnalysis] = useState(null);
  const [vaultAnalyzing, setVaultAnalyzing] = useState(false);
  const [healthForm, setHealthForm] = useState({ industry: "", monthly_revenue: "", bbb_rating: "C" });
  const [healthResult, setHealthResult] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simPdfLoading, setSimPdfLoading] = useState(false);

  const runConstructionDemo = async () => {
    setSimLoading(true);
    try {
      const { data } = await api.post("/expenses/demo/construction/run");
      setSim(data);
      toast.success("Construction company simulation ready.");
      setTimeout(() => document.getElementById("construction-sim")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch { toast.error("Could not run the simulation."); }
    finally { setSimLoading(false); }
  };

  const downloadSimPdf = async () => {
    setSimPdfLoading(true);
    try {
      const res = await api.post("/expenses/demo/construction/pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Elevare_Summit_Builders_Demo.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the report PDF."); }
    finally { setSimPdfLoading(false); }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const formatBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  const loadClientFiles = async () => {
    try {
      const { data } = await api.get(`/expenses/files?client_id=${encodeURIComponent(clientId)}`);
      setClientFiles(data.files || []);
    } catch { /* ignore silently */ }
  };

  const loadVaultAnalysis = async () => {
    try {
      const { data } = await api.get(`/expenses/vault-analysis?client_id=${encodeURIComponent(clientId)}`);
      if (data && data.result) setVaultAnalysis(data.result);
    } catch { /* ignore silently */ }
  };

  useEffect(() => { loadClientFiles(); loadVaultAnalysis(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-analysis runs server-side on upload — poll while any file is pending.
  // When the last pending file finishes, the backend auto-refreshes the
  // consolidated analysis (if one already exists), so re-pull it here too.
  const wasPendingRef = useRef(false);
  useEffect(() => {
    const hasPending = clientFiles.some((f) => f.analysis_status === "pending");
    if (wasPendingRef.current && !hasPending && vaultAnalysis) loadVaultAnalysis();
    wasPendingRef.current = hasPending;
    if (!hasPending) return;
    const t = setTimeout(loadClientFiles, 4000);
    return () => clearTimeout(t);
  }, [clientFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoApplyCsvFromFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawLines = String(e.target.result || "").split(/\r?\n/).filter((l) => l.trim());
      if (!rawLines.length) { toast.error("Empty file."); return; }
      const firstCols = splitCsv(rawLines[0]);
      const lower = firstCols.map((h) => h.toLowerCase());
      const looksHeader = lower.some((h) => /desc|name|amount|debit|payee|category|memo|item|vendor|cost|freq/.test(h));
      const rowsArr = (looksHeader ? rawLines.slice(1) : rawLines).map(splitCsv);
      const find = (opts) => (looksHeader ? lower.findIndex((h) => opts.some((o) => h.includes(o))) : -1);
      const map = {
        label: looksHeader ? find(["description", "name", "memo", "payee", "detail", "item", "vendor", "label", "merchant"]) : 0,
        amount: looksHeader ? find(["amount", "cost", "total", "value", "spent"]) : 1,
        debit: looksHeader ? find(["debit", "withdrawal", "outflow"]) : -1,
        category: looksHeader ? find(["category", "type", "class"]) : -1,
        frequency: looksHeader ? find(["frequency", "freq", "recurrence"]) : -1,
      };
      if (map.label < 0) map.label = 0;
      const built = rowsArr.map((p) => {
        const label = map.label >= 0 ? p[map.label] : "";
        let amt = NaN;
        if (map.debit >= 0 && toNum(p[map.debit])) amt = Math.abs(toNum(p[map.debit]));
        else if (map.amount >= 0) amt = Math.abs(toNum(p[map.amount]));
        const freqRaw = map.frequency >= 0 ? (p[map.frequency] || "").toLowerCase() : "monthly";
        const category = map.category >= 0 ? p[map.category] : "";
        return {
          label: (label || "").trim(),
          amount: isNaN(amt) || amt === 0 ? "" : String(amt),
          frequency: ["monthly", "weekly", "yearly", "one-time"].includes(freqRaw) ? freqRaw : "monthly",
          category: (category || "").trim(),
        };
      }).filter((r) => r.label && r.amount);
      if (!built.length) { toast.message("Uploaded — but couldn't auto-detect rows. Use Import CSV to map manually."); return; }
      setRows(built.slice(0, 60));
      toast.success(`Imported ${built.length} expenditures from ${file.name}.`);
    };
    reader.readAsText(file);
  };

  const uploadOne = async (file, label) => {
    const init = await api.post("/expenses/files/upload/init", {
      client_id: clientId, filename: file.name, mime: file.type || "application/octet-stream",
      size: file.size, business_name: businessName || undefined,
    });
    const { upload_id, chunk_size } = init.data;
    const CH = chunk_size || 5 * 1024 * 1024;
    let offset = 0, index = 0;
    while (offset < file.size) {
      const slice = file.slice(offset, offset + CH);
      const form = new FormData();
      form.append("upload_id", upload_id);
      form.append("index", index);
      form.append("chunk", slice, file.name);
      await api.post("/expenses/files/upload/chunk", form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 15 * 60 * 1000,
      });
      offset += CH; index++;
      const pct = Math.min(100, Math.round((offset / (file.size || 1)) * 100));
      setVaultProgress({ pct, loaded: Math.min(offset, file.size), total: file.size, name: `${label}${file.name}` });
    }
    const done = new FormData();
    done.append("upload_id", upload_id);
    await api.post("/expenses/files/upload/complete", done, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const uploadToVault = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > 2 * 1024 * 1024 * 1024);
    if (tooBig) { toast.error(`${tooBig.name} is over the 2 GB per-file limit.`); return; }
    setVaultUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const label = files.length > 1 ? `(${i + 1}/${files.length}) ` : "";
        setVaultProgress({ pct: 0, loaded: 0, total: f.size, name: `${label}${f.name}` });
        await uploadOne(f, label);
        if (files.length === 1 && /\.csv$/i.test(f.name)) autoApplyCsvFromFile(f);
      }
      toast.success(files.length > 1 ? `${files.length} files archived.` : `${files[0].name} archived.`);
      loadClientFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setVaultUploading(false);
      setTimeout(() => setVaultProgress(null), 800);
    }
  };

  const analyzeFile = async (f) => {
    setAnalyzingId(f.id);
    try {
      const { data } = await api.post(`/expenses/files/${f.id}/analyze`);
      setAnalyses((m) => ({ ...m, [f.id]: data.analysis }));
      toast.success(`Analyzed ${f.filename}.`);
      loadClientFiles();
    } catch (err) { toast.error(err?.response?.data?.detail || "Analysis failed."); }
    finally { setAnalyzingId(null); }
  };

  const analyzeVault = async () => {
    setVaultAnalyzing(true);
    try {
      const form = new FormData();
      form.append("client_id", clientId);
      if (businessName) form.append("business_name", businessName);
      const { data } = await api.post("/expenses/vault-analysis", form, { headers: { "Content-Type": "multipart/form-data" } });
      setVaultAnalysis(data);
      toast.success("Vault analyzed — combined breakdown ready.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not analyze the vault."); }
    finally { setVaultAnalyzing(false); }
  };

  const downloadVaultPdf = async () => {
    try {
      let chartImage = null;
      const node = document.getElementById("vault-charts-capture");
      if (node) {
        try { chartImage = await toPng(node, { backgroundColor: "#12211f", pixelRatio: 2 }); }
        catch { /* fall back to server-drawn charts */ }
      }
      const form = new FormData();
      form.append("client_id", clientId);
      if (chartImage) form.append("chart_image", chartImage);
      const res = await api.post("/expenses/vault-analysis/pdf", form, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Elevare_Expense_Analysis${businessName ? "_" + businessName.replace(/[^A-Za-z0-9]+/g, "_") : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the report. Run the analysis first."); }
  };

  const applyCategoryToWorksheet = (cat) => {
    if (!cat?.label) return;
    setRows((prev) => {
      const cleaned = prev.filter((r) => r.label.trim() || String(r.amount).trim());
      if (cleaned.some((r) => r.label.trim().toLowerCase() === cat.label.trim().toLowerCase())) {
        toast.message(`${cat.label} is already in the worksheet.`);
        return prev;
      }
      return [...cleaned, { label: cat.label, amount: String(Math.round(Number(cat.amount) || 0)),
                            frequency: "monthly", category: cat.category || "" }].slice(0, 60);
    });
    toast.success(`Added ${cat.label} to the worksheet.`);
  };

  const applyAllToWorksheet = (cats) => {
    const items = (cats || []).filter((c) => c.label);
    if (!items.length) return;
    setRows((prev) => {
      const cleaned = prev.filter((r) => r.label.trim() || String(r.amount).trim());
      const seen = new Set(cleaned.map((r) => r.label.trim().toLowerCase()));
      const add = items.filter((c) => !seen.has(c.label.trim().toLowerCase()))
        .map((c) => ({ label: c.label, amount: String(Math.round(Number(c.amount) || 0)), frequency: "monthly", category: c.category || "" }));
      return [...cleaned, ...add].slice(0, 60);
    });
    toast.success("Applied the analysis to your worksheet.");
  };

  const projectHealth = async () => {
    if (!vaultAnalysis) return;
    setHealthLoading(true);
    try {
      const costs = Number(vaultAnalysis.total_current_monthly) || 0;
      const rev = Number(healthForm.monthly_revenue) || 0;
      const margin = rev > 0 ? Math.round(((rev - costs) / rev) * 100) : 0;
      const { data } = await api.post("/predict", {
        company: businessName || "Client",
        industry: healthForm.industry || "General business",
        monthly_revenue: rev,
        monthly_costs: costs,
        bbb_rating: healthForm.bbb_rating || "C",
        profit_margin: margin,
        client_id: clientId,
      });
      setHealthResult({ ...data.result, vault_applied: data.vault_applied });
      toast.success("Business Health Score projected from your documents.");
    } catch { toast.error("Could not project the health score."); }
    finally { setHealthLoading(false); }
  };

  const isViewable = (f) => {
    const m = (f?.mime || "").toLowerCase();
    const n = (f?.filename || "").toLowerCase();
    return m === "application/pdf" || m.startsWith("image/") ||
           n.endsWith(".pdf") || /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)$/i.test(n);
  };

  const previewVaultFile = async (f) => {
    try {
      const res = await api.get(`/expenses/files/${f.id}/download`, { responseType: "blob" });
      let type = f.mime || "application/octet-stream";
      const n = (f.filename || "").toLowerCase();
      if (n.endsWith(".pdf")) type = "application/pdf";
      else if (/\.(png)$/.test(n)) type = "image/png";
      else if (/\.(jpe?g)$/.test(n)) type = "image/jpeg";
      else if (/\.(gif)$/.test(n)) type = "image/gif";
      else if (/\.(webp)$/.test(n)) type = "image/webp";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Popup blocked — allow popups to preview.");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Preview failed.";
      toast.error(msg);
    }
  };

  const downloadVaultFile = async (f) => {
    try {
      const res = await api.get(`/expenses/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.mime || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = f.filename || "file";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 800);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Download failed.";
      toast.error(msg);
    }
  };

  const deleteVaultFile = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    try {
      await api.delete(`/expenses/files/${f.id}`);
      setClientFiles((list) => list.filter((x) => x.id !== f.id));
      toast.success("File removed.");
    } catch {
      toast.error("Could not remove.");
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await api.get(`/expenses/history/${clientId}`);
      setHistory(data.history || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchHistory(); }, []);

  const fetchRecipients = async () => {
    try {
      const { data } = await api.get("/expenses/owner-config");
      setRecipients(data.summary_recipients || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchRecipients(); }, []);

  const addRecipient = () => {
    const email = recipInput.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Enter a valid email."); return; }
    if (recipients.includes(email)) { toast.message("Already in the list."); return; }
    setRecipients([...recipients, email]);
    setRecipInput("");
  };
  const removeRecipient = (email) => setRecipients(recipients.filter((r) => r !== email));

  const downloadOwnerXlsx = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const a = document.createElement("a");
    a.href = `${base}/api/expenses/owner-summary/xlsx`;
    a.download = "Elevare-Client-Report.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Downloading full client Excel report…");
  };
  const saveRecipients = async () => {
    if (!recipients.length) { toast.error("Add at least one recipient."); return; }
    setSavingRecipients(true);
    try {
      const { data } = await api.put("/expenses/owner-config", { summary_recipients: recipients });
      setRecipients(data.summary_recipients || []);
      toast.success("Summary recipients saved.");
      setShowSettings(false);
    } catch {
      toast.error("Could not save recipients.");
    } finally {
      setSavingRecipients(false);
    }
  };

  const addBudget = () => setBudgets([...budgets, { category: "", limit: "" }]);
  const updateBudget = (i, key, val) => {
    const next = [...budgets];
    next[i] = { ...next[i], [key]: val };
    setBudgets(next);
  };
  const removeBudget = (i) => setBudgets(budgets.filter((_, idx) => idx !== i));

  const loadAnalysis = async (id) => {
    try {
      const { data } = await api.get(`/expenses/analysis/${id}`);
      setResult(data.result);
      setResultId(id);
      if (data.business_name) setBusinessName(data.business_name);
      setNotes(data.notes || "");
      const cb = data.category_budgets || data.result?.category_budgets || [];
      setBudgets(cb.map((b) => ({ category: b.category || "", limit: String(b.limit ?? "") })));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load that analysis.");
    }
  };

  const sendEmail = async () => {
    if (!resultId) { toast.error("Run an analysis first."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailTo)) { toast.error("Enter a valid email."); return; }
    setEmailing(true);
    try {
      await api.post("/expenses/email", { analysis_id: resultId, to_email: emailTo });
      toast.success(`Report sent to ${emailTo}`);
      setEmailTo("");
    } catch {
      toast.error("Could not send the report.");
    } finally {
      setEmailing(false);
    }
  };

  const splitCsv = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') q = !q;
      else if (c === "," && !q) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim().replace(/^"|"$/g, ""));
  };
  const toNum = (s) => parseFloat(String(s || "").replace(/[^0-9.-]/g, ""));

  const parseCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawLines = String(e.target.result || "").split(/\r?\n/).filter((l) => l.trim());
      if (!rawLines.length) { toast.error("Empty file."); return; }
      const firstCols = splitCsv(rawLines[0]);
      const lower = firstCols.map((h) => h.toLowerCase());
      const looksHeader = lower.some((h) => /desc|name|amount|debit|payee|category|memo|item|vendor|cost|freq/.test(h));
      const headers = looksHeader ? firstCols : firstCols.map((_, i) => `Column ${i + 1}`);
      const rows = (looksHeader ? rawLines.slice(1) : rawLines).map(splitCsv);
      const find = (opts) => (looksHeader ? lower.findIndex((h) => opts.some((o) => h.includes(o))) : -1);
      const map = {
        label: looksHeader ? find(["description", "name", "memo", "payee", "detail", "item", "vendor", "label", "merchant"]) : 0,
        amount: looksHeader ? find(["amount", "cost", "total", "value", "spent"]) : 1,
        debit: looksHeader ? find(["debit", "withdrawal", "outflow"]) : -1,
        category: looksHeader ? find(["category", "type", "class"]) : -1,
        frequency: looksHeader ? find(["frequency", "freq", "recurrence"]) : -1,
      };
      if (map.label < 0) map.label = 0;
      setCsvPreview({ headers, rows, map });
    };
    reader.readAsText(file);
  };

  const setMap = (field, idx) => setCsvPreview((p) => ({ ...p, map: { ...p.map, [field]: idx } }));

  const applyMapping = () => {
    const { rows, map } = csvPreview;
    const built = rows.map((p) => {
      const label = map.label >= 0 ? p[map.label] : "";
      let amt = NaN;
      if (map.debit >= 0 && toNum(p[map.debit])) amt = Math.abs(toNum(p[map.debit]));
      else if (map.amount >= 0) amt = Math.abs(toNum(p[map.amount]));
      const freqRaw = map.frequency >= 0 ? (p[map.frequency] || "").toLowerCase() : "monthly";
      const category = map.category >= 0 ? p[map.category] : "";
      return {
        label: (label || "").trim(),
        amount: isNaN(amt) || amt === 0 ? "" : String(amt),
        frequency: ["monthly", "weekly", "yearly", "one-time"].includes(freqRaw) ? freqRaw : "monthly",
        category: (category || "").trim(),
      };
    }).filter((r) => r.label && r.amount);
    if (!built.length) { toast.error("No rows with a label and amount using this mapping."); return; }
    setRows(built.slice(0, 60));
    setCsvPreview(null);
    toast.success(`Imported ${built.length} expenditures.`);
  };

  const autoCategorize = async () => {
    const targets = rows.map((r, i) => ({ i, label: r.label.trim() })).filter((t) => t.label && !rows[t.i].category.trim());
    if (!targets.length) { toast.message("Every expense already has a category."); return; }
    setCategorizing(true);
    try {
      const { data } = await api.post("/expenses/categorize", { labels: targets.map((t) => t.label) });
      const catMap = {};
      (data.categories || []).forEach((c) => { if (c.label) catMap[c.label.toLowerCase()] = c.category; });
      const next = [...rows];
      targets.forEach((t) => { const c = catMap[t.label.toLowerCase()]; if (c) next[t.i] = { ...next[t.i], category: c }; });
      setRows(next);
      toast.success("Categories filled in.");
    } catch {
      toast.error("Could not auto-categorize.");
    } finally {
      setCategorizing(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "Description,Amount,Frequency,Category\nPayroll,28000,monthly,Labor\nFood supplier,14000,monthly,COGS\nRent,6000,monthly,Facilities\nSoftware subscriptions,900,monthly,Technology";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "elevare-expense-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    // Serve the same branded, centered worksheet PDF that clients receive by
    // email — one consistent LETTER-sized layout everywhere.
    if (!resultId) { toast.error("Run an analysis first."); return; }
    setDownloadingPdf(true);
    try {
      const { data } = await api.get(`/admin/expenses/${resultId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Elevare-Expense-Report-${(businessName || "business").replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const updateRow = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    setRows(next);
  };
  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const runningTotal = rows.reduce((sum, r) => {
    const f = { monthly: 1, weekly: 4.333, yearly: 1 / 12, "one-time": 1 / 12 }[r.frequency] || 1;
    const amt = parseFloat(r.amount);
    return sum + (isNaN(amt) ? 0 : amt * f);
  }, 0);

  const analyze = async () => {
    const expenses = rows
      .filter((r) => r.label.trim() && parseFloat(r.amount) > 0)
      .map((r) => ({
        label: r.label.trim(),
        amount: parseFloat(r.amount),
        frequency: r.frequency,
        category: r.category.trim() || undefined,
      }));
    if (expenses.length === 0) {
      toast.error("Add at least one expenditure with a label and amount.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const cleanBudgets = budgets
        .map((b) => ({ category: (b.category || "").trim(), limit: parseFloat(b.limit) }))
        .filter((b) => b.category && b.limit > 0);
      const { data } = await api.post("/expenses/analyze", {
        business_name: businessName || undefined,
        monthly_revenue: revenue ? parseFloat(revenue) : undefined,
        client_id: clientId,
        expenses,
        notes: notes.trim() || undefined,
        budgets: cleanBudgets.length ? cleanBudgets : undefined,
      });
      setResult(data.result);
      setResultId(data.id);
      toast.success("Every dollar accounted for.");
      await fetchHistory();
    } catch {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f2624] text-white" data-testid="expenses-page">
      <div className="noise-overlay" />
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="expenses-back" className="group flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-300">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" /> Back to Elevare
          </Link>
          <div className="flex items-center gap-4">
            <button data-testid="exp-settings-open" onClick={() => setShowSettings(true)}
              className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors duration-300">
              <Settings className="h-3.5 w-3.5" /> Summary recipients
            </button>
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-[#c9a961] shadow-[0_0_18px_#c9a961]" />
              <span className="font-head text-lg tracking-tight">Expense Intelligence</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-[#c9a961]" />
            <span className="text-xs uppercase tracking-[0.25em] text-white/50">Forensic AI CFO</span>
          </div>
          <h1 className="mt-6 font-head text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95] max-w-3xl text-white">
            Account for every dollar.<br /><span className="text-white/40">Then stop the leaks.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base lg:text-lg leading-relaxed text-white/60">
            Enter every expenditure. The AI maps exactly where your money goes, exposes where you&apos;re
            losing it, and builds a concrete plan to keep it.
          </p>
          <button data-testid="run-construction-demo" onClick={runConstructionDemo} disabled={simLoading}
            className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#c9a961]/50 bg-[#c9a961]/10 px-5 py-2.5 text-sm font-semibold text-[#e6cd91] hover:bg-[#c9a961]/20 disabled:opacity-50 transition-colors">
            {simLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Run construction company simulation
          </button>
        </motion.div>

        {sim && (
          <ConstructionSimulation sim={sim} fmt={fmt} onClose={() => setSim(null)}
            onDownloadPdf={downloadSimPdf} pdfLoading={simPdfLoading} />
        )}

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Input */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-6 lg:p-8">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-white/50 mb-2 block">Business</label>
                  <input data-testid="exp-business" className={inputCls} value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Acme Co." />
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-2 block">Monthly revenue ($)</label>
                  <input data-testid="exp-revenue" type="number" className={inputCls} value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder="60000" />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">Expenditures</label>
                <div className="flex items-center gap-3">
                  <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                    data-testid="exp-csv-input"
                    onChange={(e) => { if (e.target.files?.[0]) parseCsv(e.target.files[0]); e.target.value = ""; }} />
                  <button data-testid="exp-import-csv" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors duration-300">
                    <Upload className="h-3.5 w-3.5" /> Import CSV
                  </button>
                  <button data-testid="exp-csv-template" onClick={downloadTemplate}
                    className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors duration-300">
                    <FileText className="h-3.5 w-3.5" /> Sample
                  </button>
                  <span className="text-xs text-white/40">~{fmt(runningTotal)}/mo</span>
                </div>
              </div>

              {/* Client Files vault — dropzone + archive list */}
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.15em] text-white/50">Client files vault</label>
                  <span className="text-[10px] text-white/40 tabular-nums">{clientFiles.length} file{clientFiles.length === 1 ? "" : "s"} · 2 GB max each · multiple files · any format</span>
                </div>
                <div
                  data-testid="exp-vault-dropzone"
                  onDragOver={(e) => { e.preventDefault(); setVaultDragOver(true); }}
                  onDragLeave={() => setVaultDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setVaultDragOver(false);
                    if (e.dataTransfer.files?.length) uploadToVault(e.dataTransfer.files);
                  }}
                  onClick={() => !vaultUploading && vaultRef.current?.click()}
                  className={`mt-2 cursor-pointer rounded-xl border border-dashed p-4 text-center transition-colors duration-300 ${vaultDragOver ? "border-[#c9a961] bg-[#c9a961]/8" : "border-white/15 bg-white/[0.02] hover:border-white/30"}`}
                >
                  <input
                    ref={vaultRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="exp-vault-input"
                    onChange={(e) => { if (e.target.files?.length) uploadToVault(e.target.files); e.target.value = ""; }}
                  />
                  {vaultUploading ? (
                    <div className="flex flex-col gap-2" data-testid="exp-vault-uploading">
                      <span className="inline-flex items-center justify-center gap-2 text-xs text-white/70">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Uploading {vaultProgress?.name ? `“${vaultProgress.name}”` : ""} — {vaultProgress?.pct ?? 0}%
                      </span>
                      <div className="h-1.5 w-full rounded-full bg-white/8 overflow-hidden">
                        <div
                          data-testid="exp-vault-progress"
                          className="h-full rounded-full bg-[#c9a961] transition-[width] duration-200 ease-out"
                          style={{ width: `${vaultProgress?.pct ?? 0}%` }}
                        />
                      </div>
                      {vaultProgress && (
                        <span className="text-[10px] text-white/40 tabular-nums">
                          {formatBytes(vaultProgress.loaded)} / {formatBytes(vaultProgress.total)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-white/55">
                      <Upload className="h-3.5 w-3.5 inline mr-1.5" />
                      Drop CSVs, Excel, PDFs, images or any files the client sent — auto-saved and ready for AI analysis
                    </span>
                  )}
                </div>
                {clientFiles.length > 0 && (
                  <button
                    type="button"
                    data-testid="exp-analyze-vault-btn"
                    onClick={analyzeVault}
                    disabled={vaultAnalyzing}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#c9a961] px-4 py-2 text-xs font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50 transition-colors"
                  >
                    {vaultAnalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Analyze all documents with AI
                  </button>
                )}
                {vaultAnalysis && (
                  <div className="mt-3 rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 p-4" data-testid="exp-vault-analysis">
                    <div className="flex items-center gap-2 text-[#c9a961] text-xs uppercase tracking-[0.15em] mb-2"><Sparkles className="h-3.5 w-3.5" /> AI consolidated analysis · {vaultAnalysis.document_count} doc{vaultAnalysis.document_count === 1 ? "" : "s"}</div>
                    <p className="text-sm text-white/80 leading-relaxed">{vaultAnalysis.summary}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                      <span className="text-white/60">Current: <b className="text-white">{fmt(vaultAnalysis.total_current_monthly)}/mo</b></span>
                      <span className="text-white/60">After Elevare: <b className="text-[#7fd1ae]">{fmt(vaultAnalysis.total_projected_monthly)}/mo</b></span>
                      <div className="ml-auto flex items-center gap-2">
                        <button type="button" data-testid="exp-apply-all-btn" onClick={() => applyAllToWorksheet(vaultAnalysis.expense_breakdown)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-medium text-white/75 hover:border-[#c9a961] hover:text-[#c9a961] transition-colors"><ListChecks className="h-3.5 w-3.5" /> Apply all to worksheet</button>
                        <button type="button" data-testid="exp-vault-pdf-btn" onClick={downloadVaultPdf} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-white/20 transition-colors"><Download className="h-3.5 w-3.5" /> Report PDF</button>
                      </div>
                    </div>
                    {(vaultAnalysis.expense_breakdown || []).length > 0 && (
                      <VaultCharts data={vaultAnalysis} fmt={fmt} onApplyCategory={applyCategoryToWorksheet} onApplyAll={applyAllToWorksheet} />
                    )}
                    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="exp-health-card">
                      <div className="flex items-center gap-2 text-[#c9a961] text-xs uppercase tracking-[0.15em] mb-2"><Gauge className="h-3.5 w-3.5" /> Business Health Score & BBB projection</div>
                      <p className="text-[11px] text-white/50 mb-3">Grounded in your uploaded documents (current spend {fmt(vaultAnalysis.total_current_monthly)}/mo). Add revenue, industry and current BBB rating to project the score.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input data-testid="health-industry" value={healthForm.industry} onChange={(e) => setHealthForm({ ...healthForm, industry: e.target.value })} placeholder="Industry (e.g. Roofing)" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30" />
                        <input data-testid="health-revenue" type="number" value={healthForm.monthly_revenue} onChange={(e) => setHealthForm({ ...healthForm, monthly_revenue: e.target.value })} placeholder="Monthly revenue $" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30" />
                        <select data-testid="health-bbb" value={healthForm.bbb_rating} onChange={(e) => setHealthForm({ ...healthForm, bbb_rating: e.target.value })} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                          {["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "F", "NR"].map((g) => <option key={g} value={g} className="bg-[#12211f]">{g}</option>)}
                        </select>
                      </div>
                      <button data-testid="exp-project-health-btn" onClick={projectHealth} disabled={healthLoading} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#c9a961] px-4 py-2 text-xs font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50 transition-colors">
                        {healthLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gauge className="h-3.5 w-3.5" />} Project business health
                      </button>
                      {healthResult && (
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="exp-health-result">
                          <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase tracking-wide text-white/40">Health score</div><div className="text-xl font-semibold text-white tabular-nums">{healthResult.health_score}</div></div>
                          <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase tracking-wide text-white/40">Risk</div><div className="text-sm font-semibold text-white mt-1">{healthResult.risk_level}</div></div>
                          <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase tracking-wide text-white/40">Projected BBB</div><div className="text-xl font-semibold text-[#7fd1ae]">{healthResult.projected_bbb}</div></div>
                          <div className="rounded-lg bg-white/5 px-3 py-2"><div className="text-[9px] uppercase tracking-wide text-white/40">Est. annual savings</div><div className="text-sm font-semibold text-[#c9a961] mt-1 tabular-nums">{fmt(healthResult.projected_savings)}</div></div>
                          {healthResult.vault_applied && <div className="col-span-full text-[10px] text-[#7fd1ae]">✓ Grounded in {vaultAnalysis.document_count} uploaded document{vaultAnalysis.document_count === 1 ? "" : "s"}</div>}
                        </div>
                      )}
                    </div>
                    {(vaultAnalysis.recommendations || []).length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/45 mb-1.5">Recommendations</div>
                        <ul className="space-y-1.5">
                          {vaultAnalysis.recommendations.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-white/75" data-testid={`vault-rec-${i}`}>
                              <ListChecks className="h-3.5 w-3.5 mt-0.5 text-[#c9a961] shrink-0" />
                              <span><b className="text-white">{r.title}</b>{r.est_monthly_savings ? <span className="text-[#7fd1ae]"> · save ~{fmt(r.est_monthly_savings)}/mo</span> : ""} — {r.detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {clientFiles.length > 0 && (
                  <ul className="mt-3 space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {clientFiles.map((f) => (
                      <Fragment key={f.id}>
                      <li
                        data-testid={`vault-file-${f.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 hover:border-white/25 transition-colors duration-300"
                      >
                        <button
                          type="button"
                          onClick={() => downloadVaultFile(f)}
                          data-testid={`vault-download-${f.id}`}
                          className="flex items-center gap-2 min-w-0 text-left text-xs text-white/75 hover:text-[#c9a961] transition-colors duration-300"
                          title="Download"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{f.filename}</span>
                        </button>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] text-white/35 tabular-nums">{formatBytes(f.size)}</span>
                          <button
                            type="button"
                            data-testid={`vault-analyze-${f.id}`}
                            onClick={(e) => { e.stopPropagation(); analyzeFile(f); }}
                            disabled={analyzingId === f.id || f.analysis_status === "pending"}
                            title={f.analysis_status === "pending" ? "Analyzing…" : f.analyzed_at ? "Re-analyze with AI" : "Analyze with AI"}
                            className={`transition-colors duration-300 ${f.analyzed_at ? "text-[#7fd1ae] hover:text-[#c9a961]" : f.analysis_status === "failed" ? "text-[#ef4444] hover:text-[#c9a961]" : "text-white/45 hover:text-[#c9a961]"}`}
                          >
                            {(analyzingId === f.id || f.analysis_status === "pending") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : f.analyzed_at ? <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.6} /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />}
                          </button>
                          {isViewable(f) && (
                            <button
                              type="button"
                              data-testid={`vault-view-${f.id}`}
                              onClick={(e) => { e.stopPropagation(); previewVaultFile(f); }}
                              className="text-white/45 hover:text-[#c9a961] transition-colors duration-300"
                              title="View in browser"
                            >
                              <Eye className="h-3.5 w-3.5" strokeWidth={1.6} />
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`vault-delete-${f.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteVaultFile(f); }}
                            className="text-white/35 hover:text-[#ef4444] transition-colors duration-300"
                            title="Remove"
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                          </button>
                        </div>
                      </li>
                      {(analyses[f.id] || f.analysis_summary) && (
                        <li className="rounded-lg bg-[#c9a961]/[0.06] border border-[#c9a961]/20 px-3 py-2 -mt-0.5" data-testid={`vault-insight-${f.id}`}>
                          {analyses[f.id] ? (
                            <div className="text-[11px] text-white/75 leading-relaxed">
                              <span className="text-[#c9a961] font-medium">{analyses[f.id].doc_type || "Document"}</span>
                              {analyses[f.id].totals?.expenses ? <span className="text-white/50"> · expenses {fmt(analyses[f.id].totals.expenses)}</span> : ""}
                              <div className="mt-0.5">{analyses[f.id].summary}</div>
                              {(analyses[f.id].savings_opportunities || []).slice(0, 3).map((s, k) => (
                                <div key={k} className="mt-1 flex items-start gap-1.5 text-[#7fd1ae]"><TrendingDown className="h-3 w-3 mt-0.5 shrink-0" /><span>{s.label}{s.est_monthly_savings ? ` · ~${fmt(s.est_monthly_savings)}/mo` : ""}</span></div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-[11px] text-white/60 leading-relaxed"><span className="text-[#7fd1ae]">Analyzed</span> · {f.analysis_summary}</div>
                          )}
                        </li>
                      )}
                      </Fragment>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-3 space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {rows.map((r, i) => (
                  <div key={i} data-testid={`exp-row-${i}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex gap-2">
                      <input data-testid={`exp-label-${i}`} className={`${inputCls} py-2`} value={r.label} onChange={(e) => updateRow(i, "label", e.target.value)} placeholder="e.g. Payroll" />
                      <button data-testid={`exp-remove-${i}`} onClick={() => removeRow(i)} className="shrink-0 rounded-lg border border-white/10 px-2 text-white/40 hover:text-[#ef4444] hover:border-[#ef4444]/40 transition-colors duration-300" aria-label="remove">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input data-testid={`exp-amount-${i}`} type="number" className={`${inputCls} py-2`} value={r.amount} onChange={(e) => updateRow(i, "amount", e.target.value)} placeholder="$" />
                      <select data-testid={`exp-freq-${i}`} className={`${inputCls} py-2`} value={r.frequency} onChange={(e) => updateRow(i, "frequency", e.target.value)}>
                        {["monthly", "weekly", "yearly", "one-time"].map((f) => <option key={f} value={f} className="bg-[#1e3d3a]">{f}</option>)}
                      </select>
                      <input data-testid={`exp-cat-${i}`} className={`${inputCls} py-2`} value={r.category} onChange={(e) => updateRow(i, "category", e.target.value)} placeholder="category" />
                    </div>
                  </div>
                ))}
              </div>

              <button data-testid="exp-add-row" onClick={addRow} className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 py-3 text-sm text-white/60 hover:text-white hover:border-white/30 transition-colors duration-300">
                <Plus className="h-4 w-4" /> Add expenditure
              </button>

              <button data-testid="exp-autocat" onClick={autoCategorize} disabled={categorizing}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-[#a78bfa]/30 bg-[#a78bfa]/5 py-3 text-sm text-[#c4b5fd] hover:bg-[#a78bfa]/10 transition-colors duration-300 disabled:opacity-60">
                {categorizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Auto-categorize with AI
              </button>

              {/* Category budgets */}
              <div data-testid="exp-budgets" className="mt-6">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.15em] text-white/50 flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5" /> Category budgets
                  </span>
                  <button data-testid="exp-budget-add" onClick={addBudget}
                    className="flex items-center gap-1 text-xs text-white/50 hover:text-white transition-colors duration-300">
                    <Plus className="h-3.5 w-3.5" /> Add cap
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-white/35">Set a monthly cap per category. We&apos;ll flag any that go over after analysis.</p>
                {budgets.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {budgets.map((b, i) => (
                      <div key={i} className="flex gap-2" data-testid={`exp-budget-row-${i}`}>
                        <input data-testid={`exp-budget-cat-${i}`} className={`${inputCls} py-2`} value={b.category}
                          onChange={(e) => updateBudget(i, "category", e.target.value)} placeholder="e.g. Marketing" />
                        <input data-testid={`exp-budget-limit-${i}`} type="number" className={`${inputCls} py-2 w-32`}
                          value={b.limit} onChange={(e) => updateBudget(i, "limit", e.target.value)} placeholder="$/mo" />
                        <button data-testid={`exp-budget-remove-${i}`} onClick={() => removeBudget(i)}
                          className="shrink-0 rounded-lg border border-white/10 px-2 text-white/40 hover:text-[#ef4444] hover:border-[#ef4444]/40 transition-colors duration-300" aria-label="remove">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Client notes */}
              <div className="mt-6">
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 flex items-center gap-2 mb-2">
                  <StickyNote className="h-3.5 w-3.5" /> Private notes for this analysis
                </label>
                <textarea data-testid="exp-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Context, meeting takeaways, priorities to remember…"
                  className={`${inputCls} min-h-[90px] resize-none`} />
              </div>

              <button data-testid="exp-analyze" onClick={analyze} disabled={loading}
                className="mt-5 w-full rounded-full bg-[#c9a961] text-black py-4 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Analysing every dollar…</>) : "Analyse my spending"}
              </button>
            </div>

            {/* History / compare */}
            {history.length > 0 && (
              <div data-testid="exp-history" className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                <span className="text-xs uppercase tracking-[0.15em] text-white/50 flex items-center gap-2"><History className="h-4 w-4" /> Saved analyses</span>
                {history.length >= 2 && (
                  <div data-testid="exp-trend" className="mt-4">
                    <span className="text-[11px] uppercase tracking-[0.15em] text-white/40 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Monthly spend trend</span>
                    <div className="mt-2 h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={[...history].reverse().map((h) => ({
                          date: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                          spend: h.total_monthly || 0,
                          savings: h.total_potential_monthly_savings || 0,
                        }))}>
                          <defs>
                            <linearGradient id="gSpend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#c9a961" stopOpacity={0.4} />
                              <stop offset="100%" stopColor="#c9a961" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                          <Tooltip contentStyle={{ background: "#1e3d3a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => fmt(v)} />
                          <Area type="monotone" dataKey="spend" stroke="#c9a961" strokeWidth={2} fill="url(#gSpend)" name="Monthly spend" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  {history.map((h, idx) => {
                    const prev = history[idx + 1];
                    const delta = prev ? (h.total_monthly || 0) - (prev.total_monthly || 0) : null;
                    return (
                      <button key={h.id} data-testid={`exp-history-${idx}`} onClick={() => loadAnalysis(h.id)}
                        className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-white/25 transition-colors duration-300">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm text-white/80">{h.business_name || "Analysis"}</span>
                          <span className="text-xs text-white/40">{new Date(h.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                          <span className="text-white/50">Spend {fmt(h.total_monthly)}</span>
                          <span className="text-[#10b981]">save {fmt(h.total_potential_monthly_savings)}</span>
                          {delta !== null && (
                            <span className={delta < 0 ? "text-[#10b981]" : delta > 0 ? "text-[#f59e0b]" : "text-white/40"}>
                              {delta < 0 ? "▼" : delta > 0 ? "▲" : "—"} {fmt(Math.abs(delta))} vs prev
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!result && !loading && (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[520px] rounded-3xl border border-dashed border-white/10 flex flex-col items-center justify-center text-center p-10">
                  <Wallet className="h-10 w-10 text-white/20" strokeWidth={1} />
                  <p className="mt-6 text-white/40 max-w-xs">Your dollar-by-dollar breakdown, risk map and prevention plan will appear here.</p>
                </motion.div>
              )}
              {loading && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full min-h-[520px] rounded-3xl border border-white/10 bg-white/[0.02] flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 text-[#c9a961] animate-spin" />
                  <p className="text-white/50 text-sm">Tracing where every dollar goes…</p>
                </motion.div>
              )}
              {result && !loading && (
                <motion.div key="res" data-testid="exp-result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                    <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                      <div className="relative flex-1 sm:w-56">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                        <input data-testid="exp-email-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                          placeholder="partner@email.com" className={`${inputCls} pl-9 py-2.5`} />
                      </div>
                      <button data-testid="exp-email-send" onClick={sendEmail} disabled={emailing}
                        className="shrink-0 rounded-full bg-[#c9a961] text-black px-4 py-2.5 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center gap-2">
                        {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email
                      </button>
                    </div>
                    <button data-testid="exp-download-pdf" onClick={downloadPdf} disabled={downloadingPdf}
                      className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm text-white/80 hover:text-white hover:border-white/50 transition-colors duration-300 disabled:opacity-60">
                      {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                      <span className="text-xs text-white/50">Total monthly spend</span>
                      <div className="mt-2 font-head text-4xl font-light">{fmt(result.total_monthly)}</div>
                    </div>
                    <div className="rounded-2xl border border-[#10b981]/30 bg-[#10b981]/5 p-6">
                      <span className="text-xs text-white/50">Potential monthly savings</span>
                      <div className="mt-2 font-head text-4xl font-light text-[#10b981]">{fmt(result.total_potential_monthly_savings)}</div>
                    </div>
                  </div>

                  <p className="text-sm leading-relaxed text-white/70 rounded-2xl border border-white/10 bg-white/[0.03] p-6">{result.summary}</p>

                  {/* Elevare Business Health Score */}
                  {result.business_health?.scores?.length > 0 && (
                    <div data-testid="exp-health-score" className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                      <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2">
                        <Gauge className="h-4 w-4" /> Elevare Business Health Score<sup className="text-[9px]">TM</sup>
                      </span>
                      <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-white/[0.07]">
                              <th className="text-left px-4 py-2.5 font-semibold text-white">Category</th>
                              <th className="text-right px-4 py-2.5 font-semibold text-white">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.business_health.scores.map((s, idx) => (
                              <tr key={idx} data-testid={`exp-health-row-${idx}`} className="border-t border-white/[0.08]">
                                <td className="px-4 py-2.5 text-white/80">{s.category}</td>
                                <td className="px-4 py-2.5 text-right text-white font-semibold tabular-nums">{s.score}/100</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-5 space-y-2.5">
                        {[
                          { key: "highest_priority", label: "Highest Priority", color: "#ef4444" },
                          { key: "opportunity", label: "Opportunity", color: "#eab308" },
                          { key: "strength", label: "Strength", color: "#22c55e" },
                        ].map(({ key, label, color }) => {
                          const item = result.business_health[key];
                          if (!item) return null;
                          return (
                            <div key={key} data-testid={`exp-health-${key}`} className="flex items-center gap-2.5 text-sm">
                              <span className="h-3 w-3 rounded-full shrink-0" style={{ background: color }} />
                              <span className="font-semibold text-white">{label}</span>
                              <span className="text-white/60">{item.category} — {item.score}/100</span>
                            </div>
                          );
                        })}
                      </div>
                      {result.business_health.recommendation && (
                        <div data-testid="exp-health-recommendation" className="mt-5 rounded-xl border border-[#c9a961]/25 bg-[#c9a961]/[0.06] p-4">
                          <span className="text-[11px] uppercase tracking-[0.2em] text-[#c9a961]">Elevare&apos;s Recommendation</span>
                          <p className="mt-2 text-sm leading-relaxed text-white/75">{result.business_health.recommendation}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Client notes */}
                  {notes && notes.trim() && (
                    <div data-testid="exp-result-notes" className="rounded-2xl border border-[#a78bfa]/25 bg-[#a78bfa]/[0.06] p-5">
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[#c4b5fd] flex items-center gap-2">
                        <StickyNote className="h-3.5 w-3.5" /> Your notes
                      </span>
                      <p className="mt-2 text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{notes}</p>
                    </div>
                  )}

                  {/* Overspend flags */}
                  {result.overspends && result.overspends.length > 0 && (
                    <div data-testid="exp-overspends" className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                      <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> Budget vs actual
                      </span>
                      <div className="mt-4 space-y-3">
                        {result.overspends.map((o, idx) => (
                          <div key={idx} data-testid={`exp-overspend-${idx}`}
                            className={`rounded-xl border p-4 flex items-center justify-between gap-3 ${o.over ? "border-[#ef4444]/40 bg-[#ef4444]/10" : "border-[#10b981]/30 bg-[#10b981]/[0.06]"}`}>
                            <div>
                              <div className="text-sm text-white/85 font-medium">{o.category}</div>
                              <div className="text-xs text-white/50 mt-0.5">Cap {fmt(o.limit)} · Spent {fmt(o.spent)}</div>
                            </div>
                            {o.over ? (
                              <span className="text-xs font-semibold text-[#ef4444] rounded-full border border-[#ef4444]/40 px-3 py-1">
                                Over by {fmt(o.over_by)}
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-[#10b981] rounded-full border border-[#10b981]/40 px-3 py-1">
                                Under by {fmt(Math.abs(o.over_by))}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Allocation */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2"><Wallet className="h-4 w-4" /> Where every dollar goes</span>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                      <div className="flex items-center justify-center">
                        <PieChart width={200} height={200}>
                          <Pie data={result.allocation} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2} stroke="none" isAnimationActive={false}>
                            {result.allocation?.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#1e3d3a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => fmt(v)} />
                        </PieChart>
                      </div>
                      <div className="space-y-2">
                        {result.allocation?.map((a, idx) => (
                          <div key={idx} data-testid={`exp-alloc-${idx}`} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-white/70">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                              {a.category}
                            </span>
                            <span className="text-white/50">{fmt(a.amount)} · {Math.round(a.percent)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Risks */}
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Where you&apos;re losing money</span>
                    <div className="mt-4 space-y-4">
                      {result.risks?.map((rk, idx) => (
                        <div key={idx} data-testid={`exp-risk-${idx}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                          <div className="flex items-start justify-between gap-3">
                            <h4 className="font-head text-base leading-snug text-white">{rk.area}</h4>
                            <span className="shrink-0 text-[10px] uppercase tracking-wide rounded-full px-2 py-1 border" style={{ color: sev[rk.severity], borderColor: `${sev[rk.severity]}66` }}>{rk.severity}</span>
                          </div>
                          <p className="mt-1 text-xs text-[#ef4444] flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> ~{fmt(rk.monthly_loss_estimate)}/mo at risk</p>
                          <p className="mt-3 text-sm leading-relaxed text-white/55">{rk.why}</p>
                          <div className="mt-4 flex gap-3 rounded-xl bg-[#10b981]/5 border border-[#10b981]/20 p-4">
                            <ShieldAlert className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" />
                            <p className="text-sm leading-relaxed text-white/75">{rk.prevention}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plan */}
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50 flex items-center gap-2"><ListChecks className="h-4 w-4" /> Your prevention plan</span>
                    <div className="mt-4 relative pl-8">
                      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" />
                      {result.action_plan?.map((s, idx) => (
                        <div key={idx} data-testid={`exp-plan-${idx}`} className="relative mb-5">
                          <span className="absolute -left-8 top-1.5 h-4 w-4 rounded-full border-2 border-[#c9a961] bg-[#0f2624]" />
                          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                            <div className="flex items-center justify-between gap-3">
                              <h4 className="font-head text-base text-white">{s.step}</h4>
                              <span className="text-xs text-[#10b981] shrink-0">+{fmt(s.expected_monthly_savings)}/mo</span>
                            </div>
                            <p className="mt-2 text-sm leading-relaxed text-white/55 flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" /> {s.detail}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <BusinessIntelAssessment
                    rows={rows}
                    revenue={revenue}
                    notes={notes}
                    clientId={clientId}
                    businessName={businessName}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* CSV column mapping preview */}
      <AnimatePresence>
        {csvPreview && (
          <motion.div
            data-testid="csv-preview-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setCsvPreview(null)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-2xl rounded-3xl border border-white/10 bg-[#1e3d3a] p-8 max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-head text-2xl text-white">Confirm column mapping</h3>
              <p className="mt-2 text-sm text-white/50">We matched your CSV columns automatically. Adjust any that look wrong, then import.</p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "label", label: "Expense name", optional: false },
                  { key: "amount", label: "Amount", optional: true },
                  { key: "debit", label: "Debit column (optional)", optional: true },
                  { key: "category", label: "Category (optional)", optional: true },
                  { key: "frequency", label: "Frequency (optional)", optional: true },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-xs text-white/50 mb-2 block">{f.label}</label>
                    <select
                      data-testid={`csv-map-${f.key}`}
                      className={`${inputCls} py-2.5`}
                      value={csvPreview.map[f.key]}
                      onChange={(e) => setMap(f.key, parseInt(e.target.value, 10))}
                    >
                      {f.optional && <option value={-1} className="bg-[#1e3d3a]">— None —</option>}
                      {csvPreview.headers.map((h, i) => (
                        <option key={i} value={i} className="bg-[#1e3d3a]">{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <span className="text-xs uppercase tracking-[0.15em] text-white/40">Preview</span>
                <div className="mt-2 rounded-xl border border-white/10 divide-y divide-white/5">
                  {csvPreview.rows.slice(0, 4).map((p, i) => {
                    const m = csvPreview.map;
                    const amt = m.debit >= 0 && toNum(p[m.debit]) ? Math.abs(toNum(p[m.debit])) : (m.amount >= 0 ? Math.abs(toNum(p[m.amount])) : NaN);
                    return (
                      <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="text-white/80 truncate">{(m.label >= 0 ? p[m.label] : "") || "—"}</span>
                        <span className="text-white/50">{isNaN(amt) ? "—" : fmt(amt)}{m.category >= 0 && p[m.category] ? ` · ${p[m.category]}` : ""}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-white/30">{csvPreview.rows.length} rows detected</p>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button data-testid="csv-cancel" onClick={() => setCsvPreview(null)}
                  className="rounded-full border border-white/20 px-6 py-3 text-sm text-white/70 hover:text-white hover:border-white/50 transition-colors duration-300">
                  Cancel
                </button>
                <button data-testid="csv-apply" onClick={applyMapping}
                  className="rounded-full bg-[#c9a961] text-black px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300">
                  Import {csvPreview.rows.length} rows
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Summary recipients settings modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            data-testid="exp-settings-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#1e3d3a] p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-head text-2xl text-white">Monthly summary recipients</h3>
              <p className="mt-2 text-sm text-white/50">Everyone here gets the auto-generated owner summary — with an Excel workbook of every client (expenses, spend plan, growth actions) attached — on the 1st of each month.</p>

              <button data-testid="exp-download-xlsx" onClick={downloadOwnerXlsx}
                className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl border border-[#10b981]/30 bg-[#10b981]/5 py-3 text-sm text-[#10b981] hover:bg-[#10b981]/10 transition-colors duration-300">
                <Download className="h-4 w-4" /> Download full client Excel report now
              </button>

              <div className="mt-6 flex gap-2">
                <input
                  data-testid="exp-recipient-input"
                  type="email"
                  className={`${inputCls} py-2.5`}
                  value={recipInput}
                  onChange={(e) => setRecipInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                  placeholder="person@company.com"
                />
                <button data-testid="exp-recipient-add" onClick={addRecipient}
                  className="shrink-0 rounded-xl border border-white/20 px-4 text-sm text-white/80 hover:text-white hover:border-white/50 transition-colors duration-300 flex items-center gap-1.5">
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>

              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                {recipients.length === 0 && (
                  <p className="text-xs text-white/40">No recipients yet.</p>
                )}
                {recipients.map((email) => (
                  <div key={email} data-testid={`exp-recipient-${email}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                    <span className="text-sm text-white/80 truncate">{email}</span>
                    <button data-testid={`exp-recipient-remove-${email}`} onClick={() => removeRecipient(email)}
                      className="shrink-0 text-white/40 hover:text-[#ef4444] transition-colors duration-300" aria-label="remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button data-testid="exp-settings-cancel" onClick={() => setShowSettings(false)}
                  className="rounded-full border border-white/20 px-6 py-3 text-sm text-white/70 hover:text-white hover:border-white/50 transition-colors duration-300">
                  Cancel
                </button>
                <button data-testid="exp-settings-save" onClick={saveRecipients} disabled={savingRecipients}
                  className="rounded-full bg-[#c9a961] text-black px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center gap-2">
                  {savingRecipients ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
